import React, { useMemo, useState } from "react";
import { Callout } from "components/common/Callout";
import { InlineError } from "components/common/InlineError";
import { Switch } from "components/common/Switch";
import { ConfirmDialog } from "components/main/utils/ConfirmDialog";
import { Spinner } from "components/common/Spinner";
import { FigureTable, type FigureRow } from "components/common/FigureTable";
import { SectionRule } from "components/common/SectionRule";
import { plural } from "components/common/setUtils";
import {
  useMemberLimit,
  usePaidSearches,
  useProviderKey,
  useProviderKeyUsage,
  useProviders,
} from "@/lib/api/apps/hooks";
import {
  spendLabel,
  TOKEN_KINDS,
  usd,
  usdMicros,
  webCalls,
} from "@/components/main/apps/app-format";
import {
  InlineFigure,
  type FigureFormat,
} from "@/components/main/apps/limit-field";
import { formatDayMonth, formatDayMonthYear } from "@/lib/dates";
import type {
  MemberLimit,
  PaidSearches,
  ProviderKeyUsage,
  ProviderKeyUsageRow,
} from "@/lib/api/apps/types";
import type { McpPeriod, Org } from "@/lib/api/organizations";
import { ProviderKeysPanel } from "@/components/main/connections/ProviderKeyList";
import {
  periodRange,
  splitTop,
  usageMember,
  type UsageMember,
} from "./memberUsage";
import { MemberFinder, PeriodSwitch } from "./UsageControls";

const TOP_MEMBERS = 3;

/** Org settings ▸ Model providers: the organization's keys for its apps
 *  (docs/AgentApps.md A29), set by an owner or admin, used by every member. */
export const ModelProvidersPanel: React.FC<{ org: Org }> = ({ org }) => {
  const [period, setPeriod] = useState<McpPeriod>("current");
  const catalog = useProviders(org.org_id, { live: true });
  const keys = useProviderKey(org.org_id);
  const usage = useProviderKeyUsage(org.org_id, period);
  if (catalog.isLoading) {
    return <Spinner />;
  }
  if (catalog.error || !catalog.data) {
    return (
      <Callout variant="error" title="The providers could not be loaded">
        {(catalog.error as Error | null)?.message ?? "no answer"}
      </Callout>
    );
  }
  return (
    <ProviderKeysPanel
      label="Model providers"
      Icon={null}
      providers={catalog.data.providers.filter((p) => p.needs_key)}
      put={keys.put}
      remove={keys.remove}
      limit={keys.limit}
    >
      {catalog.data.paid_searches?.paid_plan && (
        <PaidSearchesSection
          orgId={org.org_id}
          paid={catalog.data.paid_searches}
        />
      )}
      {usage.data && (
        <div
          className="mt-6 flex flex-col gap-4"
          style={{ opacity: usage.isPlaceholderData ? 0.5 : 1 }}
        >
          <SectionRule
            inset
            label="Usage"
            trailing={periodRange(
              Date.parse(usage.data.period_start) / 1000,
              Date.parse(usage.data.period_end) / 1000,
            )}
            end={<PeriodSwitch value={period} onChange={setPeriod} />}
          />
          <UsageBreakdown rows={usage.data.rows} />
          <MemberUsage
            orgId={org.org_id}
            usage={usage.data}
            current={period === "current"}
            personal={org.is_personal}
          />
        </div>
      )}
    </ProviderKeysPanel>
  );
};

const SEARCHES: FigureFormat = {
  show: (n) => `${n.toLocaleString("en-GB")} web lookups`,
  parse: (text) => {
    const n = Number(text.trim().replaceAll(",", ""));
    return Number.isInteger(n) && n >= 0 ? n : null;
  },
  edit: String,
  after: "web lookups",
  inputMode: "numeric",
  unit: "in web lookups",
  invalid: "Whole web lookups",
};

const per1000 = (centsPer100: number) => usd(centsPer100 * 10);

/** Web lookups past the trial: off until an owner who manages billing agrees
 *  to the terms, which the server stores with who agreed and when. */
const PaidSearchesSection: React.FC<{
  orgId: string;
  paid: PaidSearches;
}> = ({ orgId, paid }) => {
  const { toggle, limit } = usePaidSearches(orgId);
  const [confirming, setConfirming] = useState(false);
  const error = (toggle.error ?? limit.error) as Error | null;
  return (
    <div className="mt-6 flex flex-col gap-3">
      <SectionRule inset label="Paid web lookups" />
      {!paid.sold ? (
        <p className="text-fg-muted-regular-small px-1" style={{ margin: 0 }}>
          Coming soon: web lookups past the trial, billed by the 100.
        </p>
      ) : (
        <div className="flex flex-col gap-2 px-1">
          <div className="flex items-center gap-3">
            <Switch
              checked={paid.on !== null}
              disabled={!paid.can_manage || toggle.isPending}
              aria-label="Paid web lookups"
              onChange={(on) =>
                on
                  ? setConfirming(true)
                  : toggle.mutate({ on: false, centsPer100: null })
              }
            />
            <span className="text-fg-muted">
              {paid.on
                ? `On since ${formatDayMonthYear(paid.on.at)}, turned on by ${paid.on.by} at ${per1000(paid.on.cents_per_100)} per 1,000.`
                : `Off. ${per1000(paid.cents_per_100)} per 1,000 web lookups once on.`}
            </span>
          </div>
          {paid.on && (
            <div className="flex items-center gap-1.5 tabular-nums flex-wrap">
              <span className="text-fg-muted">Monthly limit</span>
              {paid.can_manage ? (
                <InlineFigure
                  value={paid.limit}
                  format={SEARCHES}
                  clearable
                  label="monthly limit on paid web lookups"
                  hint="Set monthly limit"
                  onSave={(next) => limit.mutateAsync(next)}
                />
              ) : (
                <span>
                  {paid.limit === null
                    ? "unlimited"
                    : SEARCHES.show(paid.limit)}
                </span>
              )}
              <span className="text-fg-muted">
                · {paid.used.toLocaleString("en-GB")} used, resets on{" "}
                {formatDayMonth(paid.resets_at * 1000)}
              </span>
            </div>
          )}
          {!paid.can_manage && (
            <p className="text-fg-muted-regular-small" style={{ margin: 0 }}>
              An owner who manages billing turns them on and sets the limit.
            </p>
          )}
        </div>
      )}
      {error && <InlineError className="px-1">{error.message}</InlineError>}
      <ConfirmDialog
        open={confirming}
        title="Turn on paid web lookups?"
        message={paid.terms}
        confirmText="Agree and turn on"
        isLoading={toggle.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() =>
          toggle.mutate(
            { on: true, centsPer100: paid.cents_per_100 },
            { onSettled: () => setConfirming(false) },
          )
        }
      />
    </div>
  );
};

function byModel(rows: ProviderKeyUsageRow[]): ProviderKeyUsageRow[] {
  const models = new Map<string, ProviderKeyUsageRow>();
  for (const r of rows) {
    const key = `${r.provider}/${r.model}`;
    const m = models.get(key) ?? {
      ...r,
      calls: 0,
      cost_micros: 0,
      input_tokens: 0,
      cache_read_tokens: 0,
      cache_write_tokens: 0,
      output_tokens: 0,
    };
    m.calls += r.calls;
    m.cost_micros += r.cost_micros;
    for (const [, kind] of TOKEN_KINDS) m[kind] += r[kind];
    models.set(key, m);
  }
  return [...models.values()].sort(
    (a, b) =>
      a.provider_name.localeCompare(b.provider_name) ||
      b.cost_micros - a.cost_micros,
  );
}

const tokenBreakdown = (m: ProviderKeyUsageRow) => (
  <span className="grid grid-cols-[auto_auto] gap-x-3 tabular-nums">
    {TOKEN_KINDS.map(([label, kind]) => (
      <React.Fragment key={kind}>
        <span>{label}</span>
        <span className="text-right">{m[kind].toLocaleString()}</span>
      </React.Fragment>
    ))}
  </span>
);

/** Calls and cost per model, then the runs' web lookups and page reads,
 *  then one total; hovering a model splits its tokens by kind. */
const UsageBreakdown: React.FC<{ rows: ProviderKeyUsageRow[] }> = ({
  rows,
}) => {
  const models = useMemo(() => byModel(rows), [rows]);
  const sum = (
    key: "cost_micros" | "web_cost_micros" | "searches" | "page_reads",
  ) => rows.reduce((s, r) => s + r[key], 0);
  return (
    <FigureTable
      testId="usage-breakdown"
      head={["", "Calls", "Cost"]}
      rows={[
        ...models.map((m) => ({
          key: `${m.provider}/${m.model}`,
          hint: tokenBreakdown(m),
          cells: [
            <React.Fragment key="model">
              <span className="text-fg-muted">{m.provider_name}</span> {m.model}
            </React.Fragment>,
            m.calls.toLocaleString(),
            usdMicros(m.cost_micros),
          ],
        })),
        {
          key: "web",
          cells: [
            "Web lookups",
            webCalls(sum("searches"), sum("page_reads")),
            usdMicros(sum("web_cost_micros")),
          ],
        },
        {
          key: "total",
          tone: "total",
          testId: "usage-total",
          cells: [
            "Total",
            "",
            usdMicros(sum("cost_micros") + sum("web_cost_micros")),
          ],
        },
      ]}
    />
  );
};

interface Spender extends UsageMember {
  /** `null` for someone who has left the org: no limits to set. */
  member: MemberLimit | null;
  ai: number;
  search: number;
  total: number;
}

/** Every member, with or without spend, so any of them can be found; and
 *  anyone who spent in the period and has left since. */
function spendersOf(usage: ProviderKeyUsage): Spender[] {
  const byId = new Map<string, Spender>();
  const add = (
    clientId: string,
    name: string,
    email: string,
    member: MemberLimit | null,
  ) => {
    const spender: Spender = {
      ...usageMember(clientId, name, email),
      member,
      ai: 0,
      search: 0,
      total: 0,
    };
    byId.set(clientId, spender);
    return spender;
  };
  for (const m of usage.members) add(m.client_id, m.name, m.email, m);
  for (const r of usage.rows) {
    const s = byId.get(r.client_id) ?? add(r.client_id, r.name, r.email, null);
    s.ai += r.cost_micros;
    s.search += r.web_cost_micros;
    s.total += r.cost_micros + r.web_cost_micros;
  }
  return [...byId.values()].sort(
    (a, b) => b.total - a.total || a.label.localeCompare(b.label),
  );
}

const Spend: React.FC<{
  micros: number;
  near?: boolean;
  limit?: React.ReactNode;
}> = ({ micros, near, limit }) => (
  <span className="inline-flex items-baseline justify-end gap-1.5">
    <span
      className={
        near ? "text-warning" : micros === 0 ? "text-fg-muted" : undefined
      }
    >
      {usdMicros(micros)}
    </span>
    {limit && <span className="text-fg-muted">{limit}</span>}
  </span>
);

/** Cost per member and provider, and each member's limit across the keys,
 *  edited in place. A non-personal org adds the default every member takes, the
 *  top spenders with the rest summed, and a search for anyone else. */
const MemberUsage: React.FC<{
  orgId: string;
  usage: ProviderKeyUsage;
  current: boolean;
  personal: boolean;
}> = ({ orgId, usage, current, personal }) => {
  const save = useMemberLimit(orgId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const spenders = useMemo(() => spendersOf(usage), [usage]);
  const selected = spenders.find((s) => s.clientId === selectedId) ?? null;
  const { shown, rest } = personal
    ? { shown: spenders.slice(0, 1), rest: [] }
    : splitTop(spenders, TOP_MEMBERS, selected, (s) => s.total > 0);
  const sums = (list: Spender[]) =>
    (["ai", "search", "total"] as const).map((k) =>
      usdMicros(list.reduce((n, s) => n + s[k], 0)),
    );
  /** `who` `null` is the default every member without their own takes. */
  const limitField = (who: Spender | null, own: number | null) => (
    <InlineFigure
      value={own}
      fallback={who ? usage.default_limit_cents : null}
      clearable
      prefix={who ? "/" : undefined}
      label={`limit for ${who?.label ?? "every member"}`}
      hint="Set monthly limit"
      onSave={(limitCents) =>
        save.mutateAsync({ clientId: who?.clientId ?? null, limitCents })
      }
    />
  );
  const totalOf = (s: Spender) => {
    const m = s.member;
    const applies = m?.limit_cents ?? null;
    return (
      <Spend
        key="total"
        micros={s.total}
        near={current && spendLabel(s.total, applies).near}
        limit={current && m && limitField(s, m.own_limit ? applies : null)}
      />
    );
  };
  const rows: FigureRow[] = [
    ...(current && !personal
      ? [
          {
            key: "defaults",
            tone: "muted" as const,
            cells: [
              "Default per-user limit",
              "",
              "",
              limitField(null, usage.default_limit_cents),
            ],
          },
        ]
      : []),
    ...shown.map((s) => ({
      key: s.clientId,
      testId: "member-usage-row",
      tone: s === selected ? ("selected" as const) : undefined,
      cells: [
        s.label,
        <Spend key="ai" micros={s.ai} />,
        <Spend key="search" micros={s.search} />,
        totalOf(s),
      ],
    })),
    ...(rest.length > 0
      ? [
          {
            key: "rest",
            tone: "muted" as const,
            cells: [plural(rest.length, "more member"), ...sums(rest)],
          },
        ]
      : []),
    ...(personal
      ? []
      : [
          {
            key: "total",
            tone: "total" as const,
            cells: ["Total", ...sums(spenders)],
          },
        ]),
  ];

  return (
    <div className="flex flex-col gap-2">
      {!personal && (
        <MemberFinder
          members={spenders}
          selectedId={selectedId}
          onSelect={setSelectedId}
          label="Find a member"
        />
      )}
      <FigureTable
        testId="member-usage"
        head={[personal ? "" : "Member", "AI", "Web lookups", "Total"]}
        rows={rows}
      />
    </div>
  );
};
